import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppContent from './app';
import { resolvePageTitle, resolvePageDescription } from '../config/pageTitles';
import LoginPage from './routes/LoginPage';
import ResetPasswordRoute from './routes/ResetPasswordRoute';
import SetPasswordRoute from './routes/SetPasswordRoute';
import MagicLinkCallbackRoute from './routes/MagicLinkCallbackRoute';
import LinkedInCallbackRoute from './routes/LinkedInCallbackRoute';
import GoogleCallbackRoute from './routes/GoogleCallbackRoute';
import DashboardPage from './routes/DashboardPage';
import CaseStudyPage from './routes/CaseStudyPage';
import CaseQuizPageRoute from './routes/CaseQuizPageRoute';
import CaseTutorialPageRoute from './routes/CaseTutorialPageRoute';
import CaseDiscussionPageRoute from './routes/CaseDiscussionPageRoute';
import ProfilePageRoute from './routes/ProfilePageRoute';
import TrackerPageRoute from './routes/TrackerPageRoute';

// Import new tool pages
import QuizPageRoute from './routes/QuizPageRoute';
import InterviewPageRoute from './routes/InterviewPageRoute';
import InterviewLeavesPageRoute from './routes/InterviewLeavesPageRoute';
import InterviewPapersPageRoute from './routes/InterviewPapersPageRoute';
import InterviewPaperBriefPageRoute from './routes/InterviewPaperBriefPageRoute';
import InterviewSittingPageRoute from './routes/InterviewSittingPageRoute';
import InterviewFeedbackPageRoute from './routes/InterviewFeedbackPageRoute';
import InterviewReviewPageRoute from './routes/InterviewReviewPageRoute';
import WrittenPaperPageRoute from './routes/WrittenPaperPageRoute';
import ApplicationBuilderPageRoute from './routes/ApplicationBuilderPageRoute';
import QuizGenerationPageRoute from './routes/QuizGenerationPageRoute';
import WebsiteQuizRoute from './routes/WebsiteQuizRoute';
import WebsiteInterviewPageRoute from './routes/WebsiteInterviewPageRoute';
import WebsiteReasoningPageRoute from './routes/WebsiteReasoningPageRoute';
import AppsStorePageRoute from './routes/AppsStorePageRoute';
import AppStoreViewRoute from './routes/AppStoreViewRoute';
import MarketplaceViewRoute from './routes/MarketplaceViewRoute';
import AppStoreMarketplacePageRoute from './routes/AppStoreMarketplacePageRoute';
import ClassicPlayStorePageRoute from './routes/ClassicPlayStorePageRoute';
import MyAppsPageRoute from './routes/MyAppsPageRoute';
import AppDetailPageRoute from './routes/AppDetailPageRoute';
import AppExperienceRoute from './routes/AppExperienceRoute';
import AppTestsRoute from './routes/AppTestsRoute';
import StoreAppDetailRoute from './routes/StoreAppDetailRoute';
import CreatorPageRoute from './routes/CreatorPageRoute';
import StoreCreatorPageRoute from './routes/StoreCreatorPageRoute';
import CreatorsPageRoute from './routes/CreatorsPageRoute';
import StoreCreatorsPageRoute from './routes/StoreCreatorsPageRoute';
import WindmillGameRoute from './routes/WindmillGameRoute';
import AppleGameRoute from './routes/AppleGameRoute';
import SputnikGameRoute from './routes/SputnikGameRoute';
import GameStudioRoute from './routes/GameStudioRoute';
import FlashCardsRoute from './routes/FlashCardsRoute';
import NotesRoute from './routes/NotesRoute';
import NotesRedirect from './routes/NotesRedirect';
import CustomQuizGeneratorRoute from './routes/CustomQuizGeneratorRoute';
import ChatRoute from './routes/ChatRoute';
import TaskListRoute from './routes/TaskListRoute';
import TaskListRedirect from './routes/TaskListRedirect';
import StudioRoute from './routes/StudioRoute';
import StudioLandingRoute from './routes/StudioLandingRoute';
import StudioCasesRoute from './routes/StudioCasesRoute';
import ExamStudioRoute from './routes/ExamStudioRoute';
import TeacherDashboardRoute from './routes/TeacherDashboardRoute';
import TeacherToolsRoute from './routes/TeacherToolsRoute';
import SubscriptionSuccessPage from './routes/SubscriptionSuccessPage';
import AdminContentManagerPage from './routes/AdminContentManagerPage';
import AllAppsInternalRoute from './routes/AllAppsInternalRoute';
import CertificateVerificationRoute from './routes/CertificateVerificationRoute';
import JoinOrganisationPage from '../features/organisations/components/JoinOrganisationPage';

import LegalHubLayout from '../components/PolicyPages/LegalHubLayout';
import SupportCenter from '../components/SupportCenter/SupportCenter';
import NotFoundPage from '../components/NotFoundPage/NotFoundPage';
import { useAuth } from '../features/auth/hooks/useAuth';
import { isEduPortal, isPathbuddyHost } from '../config/eduHost';
import PageView from '../features/marketing/PageView';
import { lazyPages, marketingRoutes } from '../features/marketing/marketingRoutes';
import { HostGate } from '../features/marketing/HostGate';
import { readHostRedirectMap, redirectSearch, resolveHostRedirect } from '../features/marketing/hostRedirects';
import { servesEduAtRoot } from '../config/appBase';
import { CASE_STUDIES_INDEX_TARGET } from '../features/marketing/redirects';
import Sidebar from '../components/Sidebar/Sidebar';
import ProductBackProvider from '../components/ProductBackButton/ProductBackProvider';
import CertPortalPage from '../features/certificates/components/CertPortalPage';
import EventVaultPage from '../features/events/components/EventVaultPage';
import DeadlineCheckerPage from '../features/admissions-tools/components/DeadlineCheckerPage';
import PathsRedirect, { PATHS_REDIRECTS } from './routes/PathsRedirect';

/**
 * Signed-in visitors on the edu portal's home go straight to the chat.
 * pathbuddy.com `/` keeps its marketing home.
 * Under the /edu basename `pathname` is router-relative, so the Edu home
 * (ivoriai.com/, and localhost:3000/edu) is "/" here, and <Navigate
 * to="/chat"> lands on /edu/chat.
 * arrivedFromRoot: the page was opened at ivoriai.com/, so the visitor sees
 * the Edu home (with "Open app") instead of the chat. An in-app link to the
 * home still bounces.
 */
export function shouldBounceToChat({ isAuthenticated, pathname, hostname, browserPath, arrivedFromRoot }) {
  if (!isAuthenticated || pathname !== '/' || arrivedFromRoot) return false;
  if (isPathbuddyHost(hostname)) return false;
  return isEduPortal(hostname, browserPath);
}

// Legal paths and the document each opens by default.
export const LEGAL_ROUTES = [
  ['/privacy', 'privacy'],
  ['/terms-of-use', 'terms'],
  ['/terms', 'terms'],
  ['/safeguarding', 'security'],
  ['/cookies', 'cookies'],
  ['/legal/*', 'privacy'],
];

const AppRouter = () => {
  // Must use isAuthenticated (not merely `user`): leftover profile JSON in
  // storage without a valid session used to send `/` → `/chat` → `/login`,
  // so visitors never saw the marketing home (esp. on pathbuddy.com).
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Synchronize dynamic, unique document title & meta description for every route and subdomain
  React.useEffect(() => {
    const title = resolvePageTitle(location.pathname, window.location.hostname);
    const description = resolvePageDescription(location.pathname, window.location.hostname);
    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute('content', description);
    }
  }, [location.pathname]);

  // Immediately synchronize window focus and scroll so keyboard arrows (Up/Down) work without needing to click
  React.useEffect(() => {
    // Footer Chat/Apps/Cases switches stay mounted in ChatShell — blur/focus here
    // steals the just-clicked tab and feels laggy. ChatShell focuses the pane itself.
    const footerTabs = ['/chat', '/apps', '/cases'];
    if (footerTabs.includes(location.pathname)) return;

    window.scrollTo(0, 0);
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    window.focus();
  }, [location.pathname]);
  
  // True when this page load opened ivoriai.com/ itself; dropped when the
  // visitor leaves the Edu home, so a later in-app "/" bounces as usual.
  const [arrivedFromRoot, setArrivedFromRoot] = useState(() => (
    typeof window !== 'undefined' && servesEduAtRoot(window.location.hostname) && window.location.pathname === '/'
  ));
  React.useEffect(() => {
    if (location.pathname !== '/') setArrivedFromRoot(false);
  }, [location.pathname]);

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  // Exam-host marketing paths, including "/", replace location even when
  // the visitor is signed in. The inline script is the primary redirect;
  // this is the fallback when it did not run. It must run before the
  // apex "/" bounce, or a signed-in exam host would go to /chat instead.
  // The resolver needs the full browser path: under the /edu basename
  // location.pathname is router-relative ("/chat"), and a main-domain
  // "/chat" would be forwarded to /edu/chat again, forever.
  const browserPath = typeof window !== 'undefined' ? window.location.pathname : location.pathname;
  const hostMap = readHostRedirectMap();
  const hostTarget = resolveHostRedirect(hostname, browserPath, hostMap);
  const hostSearch = hostTarget ? redirectSearch(hostname, location.search, hostMap) : '';
  React.useEffect(() => {
    if (hostTarget) {
      window.location.replace(hostTarget + hostSearch + location.hash);
    }
  }, [hostTarget, hostSearch, location.hash]);
  if (hostTarget) return null;

  // pathbuddy.com `/` keeps today's marketing home. The edu portal's `/`
  // (ivoriai.com/edu) still opens the app. Exam paths are not in this
  // bounce (D1).
  if (shouldBounceToChat({ isAuthenticated, pathname: location.pathname, hostname, browserPath, arrivedFromRoot })) {
    return (
      <>
        <PageView />
        <Navigate to="/chat" replace />
      </>
    );
  }


  // Chat / Apps / Cases footer: only on those three top-level tabs.
  const FOOTER_TAB_PATHS = ['/chat', '/apps', '/cases'];
  const shouldHideSidebar = !FOOTER_TAB_PATHS.includes(location.pathname);

  const renderRoutes = () => (
    <Routes>
      <Route path="/" element={<AppContent />} />
      <Route path="/about" element={<AppContent />} />
      {marketingRoutes()}
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/admin" element={<AdminContentManagerPage />} />
      <Route path="/admin/content-manager" element={<AdminContentManagerPage />} />
      <Route path="/admin/plan-manager" element={<AdminContentManagerPage />} />
      {/* Internal-only catalogue — not linked in nav/UI; open via /all-apps */}
      <Route path="/all-apps" element={<AllAppsInternalRoute />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordRoute />} />
      <Route path="/set-password" element={<SetPasswordRoute />} />
      <Route path="/login/callback" element={<MagicLinkCallbackRoute />} />
      <Route path="/auth/linkedin/callback" element={<LinkedInCallbackRoute />} />
      <Route path="/auth/google/callback" element={<GoogleCallbackRoute />} />
      <Route path="/join" element={<JoinOrganisationPage />} />
      <Route path="/dashboard" element={<Navigate to="/apps" replace />} />
      {/* Onboarding renders inline inside the /chat shell (ChatShell).
          This route is kept as a redirect only, for old
          bookmarks/links/emails, not a dead 404. */}
      <Route path="/onboarding" element={<Navigate to="/chat" replace />} />
      <Route path="/teacher-dashboard" element={<TeacherDashboardRoute />} />
      <Route path="/teacher-tools" element={<TeacherToolsRoute />} />
      <Route path="/profile" element={<ProfilePageRoute />} />
      <Route path="/case-study/:id" element={<CaseStudyPage />} />
      <Route path="/case-study/:id/quizzes" element={<CaseQuizPageRoute />} />
      <Route path="/case-study/:id/tutorial" element={<CaseTutorialPageRoute />} />
      <Route path="/case-study/:id/discussion" element={<CaseDiscussionPageRoute />} />
      {/* Shared ChatShell layout: /chat|/apps|/cases keep one mount so tab
          switches sync the URL without remounting the chat panel. */}
      <Route element={<ChatRoute />}>
        <Route path="/chat" element={<></>} />
        <Route path="/apps" element={<></>} />
        <Route path="/cases" element={<></>} />
      </Route>
      <Route path="/case-studies" element={<Navigate to={CASE_STUDIES_INDEX_TARGET} replace />} />

      {/* New tool routes */}
      <Route path="/quiz" element={<QuizPageRoute />} />
      <Route path="/interview" element={<InterviewPageRoute />} />
      {/* Interview workspaces: workspace tile -> course. A course offers a live
          interview (spoken, drawn fresh) and past papers (written). Feedback
          and review follow both; for a live interview :paperId is "live". */}
      <Route path="/interviews/:workspace" element={<InterviewLeavesPageRoute />} />
      <Route path="/interviews/:workspace/:leafKey" element={<InterviewPapersPageRoute />} />
      <Route path="/interviews/:workspace/:leafKey/interview/:setId" element={<InterviewSittingPageRoute />} />
      <Route path="/interviews/:workspace/:leafKey/:paperId" element={<InterviewPaperBriefPageRoute />} />
      <Route path="/interviews/:workspace/:leafKey/:paperId/write/:sessionId" element={<WrittenPaperPageRoute />} />
      <Route path="/interviews/:workspace/:leafKey/:paperId/sit/:sessionId" element={<InterviewSittingPageRoute />} />
      <Route path="/interviews/:workspace/:leafKey/:paperId/feedback" element={<InterviewFeedbackPageRoute />} />
      <Route path="/interviews/:workspace/:leafKey/:paperId/review" element={<InterviewReviewPageRoute />} />
      <Route path="/application-builder/*" element={<ApplicationBuilderPageRoute />} />
      <Route path="/quiz-generation" element={<QuizGenerationPageRoute />} />
      <Route path="/practice" element={<QuizGenerationPageRoute />} />
      <Route path="/practice/:examName" element={<QuizGenerationPageRoute />} />
      <Route path="/practice/admissions/:examName" element={<QuizGenerationPageRoute />} />
      <Route path="/practice/interviews/:examName" element={<QuizGenerationPageRoute />} />
      <Route path="/website-quiz" element={<WebsiteQuizRoute />} />
      <Route path="/website-interview" element={<WebsiteInterviewPageRoute />} />
      <Route path="/website-reasoning-test" element={<WebsiteReasoningPageRoute />} />
      <Route path="/create" element={<StudioLandingRoute />} />
      <Route path="/studio" element={<StudioRoute />} />
      <Route path="/studio-cases" element={<StudioCasesRoute />} />
      <Route path="/studio-exams" element={<ExamStudioRoute />} />
      <Route path="/financial-modeling-lab" element={<Navigate to="/apps" replace />} />
      {/* Retired apps (D:75-77): FlowForge, Code Lab, Smart Library,
          Supercurricular Books, Caius Explore and Financial Modeler redirect
          to /apps. */}
      {/* App Store routes */}
      <Route path="/my-apps" element={<Navigate to="/apps" replace />} />
      <Route path="/my-apps-and-workspace" element={<Navigate to="/apps" replace />} />
      <Route path="/classic-store" element={<Navigate to="/apps" replace />} />
      <Route path="/playstore" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-marketplace" element={<Navigate to="/apps" replace />} />
      <Route path="/marketplace" element={<Navigate to="/apps" replace />} />
      <Route path="/app-store" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store/marketplace" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store/my-apps/:type" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store/creators" element={<CreatorsPageRoute />} />
      <Route path="/apps-store/creator/:creatorSlug" element={<CreatorPageRoute />} />
      <Route path="/apps-store/:id/windmill-game" element={<WindmillGameRoute />} />
      <Route path="/apps-store/:id/apple-game" element={<AppleGameRoute />} />
      <Route path="/apps-store/:id/sputnik-game" element={<SputnikGameRoute />} />
      <Route path="/apps-store/:id/game-studio" element={<GameStudioRoute />} />
      <Route path="/apps-store/:id/flowforge" element={<Navigate to="/apps" replace />} />
      <Route path="/flowforge" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store/:id/custom-quiz-generator" element={<CustomQuizGeneratorRoute />} />
      <Route path="/custom-quiz-generator" element={<CustomQuizGeneratorRoute />} />
      <Route path="/apps-store/:id/flash-cards" element={<FlashCardsRoute />} />
      <Route path="/flash-cards" element={<FlashCardsRoute />} />
      <Route path="/apps-store/:id/caius-explore" element={<Navigate to="/apps" replace />} />
      <Route path="/caius-explore" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store/:id/notes" element={<NotesRoute />} />
      <Route path="/apps-store/:id/task-list" element={<Navigate to="/apps" replace />} />
      <Route path="/task-list" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store/:id/code-lab" element={<Navigate to="/apps" replace />} />
      <Route path="/code-lab" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store/:id/library" element={<Navigate to="/apps" replace />} />
      <Route path="/library" element={<Navigate to="/apps" replace />} />
      <Route path="/apps-store/:id/supercurricular-books" element={<Navigate to="/apps" replace />} />
      <Route path="/supercurricular-books" element={<Navigate to="/apps" replace />} />
      <Route path="/notes" element={<NotesRoute />} />
      {/* Paths lives in /chat (?path=, ?plan=). The old /paths URLs and the
          dead /my-path link redirect there; see routes/PathsRedirect.js. */}
      {PATHS_REDIRECTS.map(({ path, to }) => (
        <Route key={path} path={path} element={<PathsRedirect to={to} />} />
      ))}
      <Route path="/application-hub" element={<TrackerPageRoute />} />
      <Route path="/tracker" element={<Navigate to="/profile?section=tracker" replace />} />
      <Route path="/apps-store/:id/experience" element={<AppExperienceRoute />} />
      <Route path="/apps-store/:id/tests" element={<AppTestsRoute />} />
      <Route path="/apps-store/:id" element={<AppDetailPageRoute />} />
      <Route path="/app/:id" element={<AppDetailPageRoute />} />
      <Route path="/apps-store" element={<AppStoreViewRoute />} />
      <Route path="/apps-store-admin" element={<AppsStorePageRoute />} />

      <Route path="/store/:id" element={<StoreAppDetailRoute />} />
      <Route path="/creators" element={<StoreCreatorsPageRoute />} />
      <Route path="/creator" element={<Navigate to="/creators" replace />} />
      <Route path="/creator/:creatorSlug" element={<StoreCreatorPageRoute />} />

      {/* Certificate Verification & Admin Portal Routes */}
      <Route path="/verify/:certId" element={<CertificateVerificationRoute />} />
      <Route path="/verify" element={<CertificateVerificationRoute />} />
      <Route path="/cert-portal" element={<CertPortalPage />} />
      <Route path="/certificate-portal" element={<CertPortalPage />} />

      {/* Event SharePoint & Media Vault Routes */}
      <Route path="/tools/deadline-checker" element={<DeadlineCheckerPage />} />
      <Route path="/event-vault" element={<EventVaultPage />} />
      <Route path="/events" element={<EventVaultPage />} />
      <Route path="/events/:eventSlug" element={<EventVaultPage />} />
      <Route path="/event-sharepoint" element={<EventVaultPage />} />

      {/* Unified Legal Center Routes: pathbuddy.com keeps LegalHubLayout,
          IVORI hosts get the same texts in the IVORI Edu design. */}
      {LEGAL_ROUTES.map(([path, id]) => (
        <Route
          key={path}
          path={path}
          element={(
            <HostGate legacy={<LegalHubLayout defaultTab={id} />}>
              <lazyPages.LegalPage defaultId={id} />
            </HostGate>
          )}
        />
      ))}
      <Route path="/support" element={<SupportCenter />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );

  if (shouldHideSidebar) {
    return (
      <>
        <PageView />
        <ProductBackProvider enabled>
          {renderRoutes()}
        </ProductBackProvider>
      </>
    );
  }

  return (
    <div className="app-root-container">
      <PageView />
      <div className="app-main-content-layout">
        {renderRoutes()}
      </div>

      <Sidebar
      isMobileOpen={isMobileOpen}
      onClose={() => setIsMobileOpen(false)}
      />
    </div>
  );
};

export default AppRouter;

